import { CompoundDetailsWithRelatedDataDomain } from 'compoundTypes'
import {
    mapToMappedCompounds,
    mapToMappedDrivers,
    mapToMappedTargets
} from 'mappers/mtcOutputMappers'
import React, { createContext, useReducer, useMemo, useContext } from 'react'
import {
    Compound,
    Driver,
    Tumor,
    Target,
    MappedDriver,
    MappedTarget
} from 'reportViewer'

// --- Types ---

interface DataState {
    compounds: Compound[]
    drivers: Driver[]
    targets: Target[]
    tumor: Tumor
    fileName: string
}

type DataAction =
    | { type: 'SET_COMPOUNDS'; payload: Compound[] }
    | { type: 'SET_DRIVERS'; payload: Driver[] }
    | { type: 'SET_TARGETS'; payload: Target[] }
    | { type: 'SET_TUMOR'; payload: Tumor }
    | { type: 'SET_FILE_NAME'; payload: string }

interface DataContextType {
    compounds: CompoundDetailsWithRelatedDataDomain[]
    drivers: MappedDriver[]
    targets: MappedTarget[]
    tumor: Tumor
    fileName: string
    setCompounds: (compounds: Compound[]) => void
    setDrivers: (drivers: Driver[]) => void
    setTargets: (targets: Target[]) => void
    setTumor: (tumor: Tumor) => void
    setFileName: (fileName: string) => void
}

// --- Reducer ---

const dataReducer = (state: DataState, action: DataAction): DataState => {
    switch (action.type) {
        case 'SET_COMPOUNDS':
            return { ...state, compounds: action.payload }
        case 'SET_DRIVERS':
            return { ...state, drivers: action.payload }
        case 'SET_TARGETS':
            return { ...state, targets: action.payload }
        case 'SET_TUMOR':
            return { ...state, tumor: action.payload }
        case 'SET_FILE_NAME':
            return { ...state, fileName: action.payload }
        default:
            return state
    }
}

// --- Context creation ---

const DataContext = createContext<DataContextType | undefined>(undefined)

// --- Provider ---

export const DataProvider: React.FC<{ children: React.ReactNode }> = ({
    children
}) => {
    const [state, dispatch] = useReducer(dataReducer, {
        compounds: [],
        drivers: [],
        targets: [],
        tumor: {} as Tumor,
        fileName: ""
    })

    const compounds = useMemo(
        () => mapToMappedCompounds(state.compounds),
        [state.compounds]
    )
    const drivers = useMemo(
        () => mapToMappedDrivers(state.drivers),
        [state.drivers]
    )
    const targets = useMemo(
        () => mapToMappedTargets(state.targets),
        [state.targets]
    )

    const setCompounds = (compounds: Compound[]) =>
        dispatch({ type: 'SET_COMPOUNDS', payload: compounds })
    const setDrivers = (drivers: Driver[]) =>
        dispatch({ type: 'SET_DRIVERS', payload: drivers })
    const setTargets = (targets: Target[]) =>
        dispatch({ type: 'SET_TARGETS', payload: targets })
    const setTumor = (tumor: Tumor) =>
        dispatch({ type: 'SET_TUMOR', payload: tumor })
    const setFileName = (fileName: string) =>
        dispatch({ type: 'SET_FILE_NAME', payload: fileName})

    const contextValue = useMemo<DataContextType>(
        () => ({
            compounds,
            drivers,
            targets,
            tumor: state.tumor,
            fileName: state.fileName,
            setCompounds,
            setDrivers,
            setTargets,
            setTumor,
            setFileName
        }),
        [compounds, drivers, targets, state.tumor, state.fileName]
    )

    return (
        <DataContext.Provider value={contextValue}>
            {children}
        </DataContext.Provider>
    )
}

// --- Hook to consume context ---

export const useDataContext = () => {
    const context = useContext(DataContext)
    if (!context) {
        throw new Error('useDataContext must be used within a DataProvider')
    }
    return context
}
